import {forwardRef, useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomActionButton, {atomActionButtonStyles} from '@s-ui/react-atom-action-button'
import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonElevations,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import IconHeartOutline from '@adv-mt/mt-svg-icons/lib/HeartOutline'

import AdFavoriteButtonIcon from './actions-ad-favorite-button-icon.js'
import PriceDropButton from './actions-ad-favorite-price-drop-button.js'
import PriceDropInlineButton from './actions-ad-favorite-price-drop-inline-button.js'

const BASE_CLASS = 'mt-actionAdFavorite'
const BUTTON_TEXT_CLASS = `${BASE_CLASS}-favoriteButtonText`
const BUTTON_WRAPPER_CLASS = `${BASE_CLASS}-buttonWrapper`

export const TYPES = {
  ACTION_BUTTON: 'actionButton',
  INLINE_BUTTON: 'inlineButton',
  ICON_BUTTON: 'iconButton',
  ICON_BUTTON_NEGATIVE: 'iconButtonNegative',
  OVERLAY_ICON_BUTTON: 'overlayIconButton',
  PRICE_DROP_BUTTON: 'priceDropButton',
  PRICE_DROP_MENU_BUTTON: 'priceDropMenuButton',
  PRICE_DROP_INLINE_BUTTON: 'priceDropInlineButton'
}

const ActionAdFavoriteButton = forwardRef(({isMarked, onClick, size, type, className = ''}, ref) => {
  const {i18n} = useContext(Context)

  const classNameFavoriteText = cx(BUTTON_TEXT_CLASS, {
    [`${BUTTON_TEXT_CLASS}--added`]: isMarked
  })
  const classNameButtonWrapper = cx(BUTTON_WRAPPER_CLASS, className)

  const commonButtonProps = {
    'aria-checked': isMarked,
    'aria-label': i18n.t('COMMON.FAVORITE'),
    color: isMarked && atomButtonColors.ACCENT,
    onClick,
    role: 'checkbox',
    size
  }

  switch (true) {
    case type === TYPES.ICON_BUTTON:
      return (
        <div className={classNameButtonWrapper}>
          <AtomButton
            ref={ref}
            shape={atomButtonShapes.CIRCULAR}
            design={atomButtonDesigns.FLAT}
            leftIcon={<AdFavoriteButtonIcon isMarked={isMarked} />}
            {...commonButtonProps}
          />
        </div>
      )
    case type === TYPES.ICON_BUTTON_NEGATIVE:
      return (
        <AtomButton
          shape={atomButtonShapes.CIRCULAR}
          design={atomButtonDesigns.FLAT}
          negative
          leftIcon={isMarked ? <AdFavoriteButtonIcon isMarked={isMarked} /> : <IconHeartOutline />}
          {...commonButtonProps}
          color={atomButtonColors.PRIMARY}
        />
      )
    case type === TYPES.OVERLAY_ICON_BUTTON:
      return (
        <div className={classNameButtonWrapper}>
          <AtomButton
            ref={ref}
            shape={atomButtonShapes.CIRCULAR}
            design={atomButtonDesigns.SOLID}
            elevation={atomButtonElevations.MEDIUM}
            negative
            leftIcon={<AdFavoriteButtonIcon isMarked={isMarked} />}
            {...commonButtonProps}
            color={isMarked ? atomButtonColors.ACCENT : atomButtonColors.NEUTRAL}
          />
        </div>
      )
    case type === TYPES.INLINE_BUTTON:
      return (
        <AtomButton
          ref={ref}
          shape={atomButtonShapes.CIRCULAR}
          design={atomButtonDesigns.FLAT}
          leftIcon={<AdFavoriteButtonIcon isMarked={isMarked} />}
          {...commonButtonProps}
        >
          {i18n.t('COMMON.FAVORITE')}
        </AtomButton>
      )
    case type === TYPES.PRICE_DROP_BUTTON:
      return <PriceDropButton isMarked={isMarked} commonButtonProps={commonButtonProps} />
    case type === TYPES.PRICE_DROP_MENU_BUTTON:
      return <PriceDropButton isMarked={isMarked} commonButtonProps={commonButtonProps} isMenu />
    case type === TYPES.PRICE_DROP_INLINE_BUTTON:
      return <PriceDropInlineButton isMarked={isMarked} commonButtonProps={commonButtonProps} />
    default:
      return (
        <AtomActionButton
          ref={ref}
          icon={<AdFavoriteButtonIcon isMarked={isMarked} />}
          style={atomActionButtonStyles.FLAT}
          {...commonButtonProps}
        >
          <span className={classNameFavoriteText}>{i18n.t('COMMON.FAVORITE')}</span>
        </AtomActionButton>
      )
  }
})

ActionAdFavoriteButton.displayName = 'ActionAdFavoriteButton'

ActionAdFavoriteButton.propTypes = {
  isMarked: PropTypes.bool,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(Object.values(TYPES)),
  size: PropTypes.oneOf(Object.values(atomButtonSizes)),
  className: PropTypes.string
}

export default ActionAdFavoriteButton
